import { createTheme } from '@mui/material';

// ─── Design Tokens ──────────────────────────────────────────────────────────
// Change values here to restyle the entire app from one place.

const tokens = {
  // Color palette
  colors: {
    primary:       '#c0392b',
    primaryDark:   '#a93226',
    primaryLight:  '#d4524a',

    secondary:     '#f2ede6',   // warm beige for card fills / sections

    success:       '#16A34A',
    successLight:  '#F0FDF4',
    warning:       '#F59E0B',
    warningLight:  '#FEF3C7',
    info:          '#3B82F6',
    infoLight:     '#EFF6FF',
    error:         '#B91C1C',
    errorLight:    '#e8d5d2',

    bgDefault:     '#faf8f5',   // warm cream page background
    bgPaper:       '#ffffff',

    textPrimary:   '#1a1614',
    textSecondary: '#5a5250',
    muted:         '#9a918e',   // placeholders, disabled icons

    divider:       '#ddd5cc',   // borders, separators
    surface:       '#f2ede6',   // card / section fill (alias of secondary)

    // Button fills (editorial: dark buttons, not red)
    btnContained:      '#1a1614',
    btnContainedHover: '#2d2825',
    btnContainedText:  '#faf8f5',
  },

  // Typography
  fonts: {
    heading: '"Libre Baskerville", Georgia, serif',
    body:    '"DM Sans", "Helvetica", "Arial", sans-serif',
  },

  // Shape
  radii: {
    xs:   0.5,   // 2px  — buttons, cards, chips, inputs
    sm:   1,     // 4px  — dialogs, images, progress bars
    full: '50%', // circular avatars
  },

  // Transitions
  transition: {
    fast:   'all 0.2s',
    medium: 'all 0.25s ease',
    slow:   'all 0.3s ease',
  },

  // Shadows (flat design — only subtle hover hint)
  shadows: {
    none:  'none',
    hover: '0 2px 16px rgba(192,57,43,0.08)',
  },

  // Icon sizes
  icon: {
    sm: 14,
    md: 18,
    lg: 24,
  },
} as const;

// ─── MUI Theme ──────────────────────────────────────────────────────────────

const theme = createTheme({
  palette: {
    primary: {
      main:  tokens.colors.primary,
      dark:  tokens.colors.primaryDark,
      light: tokens.colors.primaryLight,
    },
    secondary: {
      main: tokens.colors.secondary,
    },
    success: {
      main:  tokens.colors.success,
      light: tokens.colors.successLight,
    },
    warning: {
      main:  tokens.colors.warning,
      light: tokens.colors.warningLight,
    },
    info: {
      main:  tokens.colors.info,
      light: tokens.colors.infoLight,
    },
    error: {
      main:  tokens.colors.error,
      light: tokens.colors.errorLight,
    },
    background: {
      default: tokens.colors.bgDefault,
      paper:   tokens.colors.bgPaper,
    },
    text: {
      primary:   tokens.colors.textPrimary,
      secondary: tokens.colors.textSecondary,
    },
    divider: tokens.colors.divider,
  },

  shape: {
    borderRadius: 4,
  },

  typography: {
    fontFamily: tokens.fonts.body,
    h1: {
      fontFamily: tokens.fonts.heading,
      fontWeight: 700,
      fontSize: '2.2rem',
      lineHeight: 1.15,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontFamily: tokens.fonts.heading,
      fontWeight: 700,
      fontSize: '1.8rem',
      lineHeight: 1.15,
      letterSpacing: '-0.02em',
    },
    h3: {
      fontFamily: tokens.fonts.heading,
      fontWeight: 700,
      fontSize: '1.5rem',
      lineHeight: 1.15,
      letterSpacing: '-0.02em',
    },
    h4: {
      fontFamily: tokens.fonts.heading,
      fontWeight: 700,
      fontSize: '1.25rem',
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h5: {
      fontFamily: tokens.fonts.heading,
      fontWeight: 400,
      fontSize: '1.1rem',
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h6: {
      fontFamily: tokens.fonts.heading,
      fontWeight: 400,
      fontSize: '0.95rem',
      lineHeight: 1.4,
      letterSpacing: '-0.01em',
    },
    body1: {
      fontFamily: tokens.fonts.body,
      fontWeight: 400,
      fontSize: '0.9rem',
      lineHeight: 1.7,
    },
    body2: {
      fontFamily: tokens.fonts.body,
      fontWeight: 300,
      fontSize: '0.85rem',
      lineHeight: 1.7,
      color: tokens.colors.textSecondary,
    },
    subtitle1: {
      fontFamily: tokens.fonts.body,
      fontWeight: 500,
      fontSize: '0.9rem',
      lineHeight: 1.4,
    },
    subtitle2: {
      fontFamily: tokens.fonts.body,
      fontWeight: 500,
      fontSize: '0.8rem',
      lineHeight: 1.4,
    },
    caption: {
      fontFamily: tokens.fonts.body,
      fontWeight: 500,
      fontSize: '0.68rem',
      letterSpacing: '0.08em',
      textTransform: 'uppercase' as const,
      color: tokens.colors.muted,
    },
    overline: {
      fontFamily: tokens.fonts.body,
      fontWeight: 500,
      fontSize: '0.6rem',
      letterSpacing: '0.1em',
      textTransform: 'uppercase' as const,
      color: tokens.colors.muted,
    },
    button: {
      fontFamily: tokens.fonts.body,
      fontWeight: 500,
      fontSize: '0.8rem',
      letterSpacing: '0.04em',
      textTransform: 'none' as const,
    },
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          scrollBehavior: 'smooth',
        },
      },
    },

    MuiButton: {
      defaultProps: {
        disableElevation: true,
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          borderRadius: tokens.radii.xs * 4,
          padding: '10px 28px',
          boxShadow: tokens.shadows.none,
          transition: tokens.transition.fast,
        },
        sizeSmall: {
          padding: '6px 16px',
          fontSize: '0.72rem',
        },
        sizeLarge: {
          padding: '12px 36px',
          fontSize: '0.85rem',
        },
        containedPrimary: {
          backgroundColor: tokens.colors.btnContained,
          color: tokens.colors.btnContainedText,
          '&:hover': {
            backgroundColor: tokens.colors.btnContainedHover,
            boxShadow: tokens.shadows.none,
          },
        },
        outlined: {
          borderColor: tokens.colors.divider,
          color: tokens.colors.textPrimary,
          '&:hover': {
            borderColor: tokens.colors.textPrimary,
            backgroundColor: 'rgba(26,22,20,0.03)',
          },
        },
      },
    },

    MuiIconButton: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          transition: tokens.transition.fast,
        },
      },
    },

    MuiCard: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderRadius: tokens.radii.xs * 4,
          border: `1px solid ${tokens.colors.divider}`,
          boxShadow: tokens.shadows.none,
          backgroundImage: 'none',
          transition: tokens.transition.fast,
        },
      },
    },

    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '20px',
          '&:last-child': {
            paddingBottom: '20px',
          },
        },
      },
    },

    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          boxShadow: tokens.shadows.none,
          backgroundImage: 'none',
          border: `1px solid ${tokens.colors.divider}`,
          borderRadius: tokens.radii.xs * 4,
        },
      },
    },

    MuiAppBar: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          boxShadow: tokens.shadows.none,
        },
      },
    },

    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: '48px !important',
          paddingLeft: '16px !important',
          paddingRight: '16px !important',
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radii.xs * 4,
          fontSize: '0.85rem',
          '& fieldset': {
            borderColor: tokens.colors.divider,
          },
          '&:hover fieldset': {
            borderColor: tokens.colors.muted,
          },
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: tokens.colors.muted,
          fontSize: '0.85rem',
        },
      },
    },

    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&.Mui-checked': {
            color: tokens.colors.primary,
          },
        },
      },
    },

    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': {
            color: tokens.colors.primary,
          },
          '&.Mui-checked + .MuiSwitch-track': {
            backgroundColor: tokens.colors.primary,
          },
        },
      },
    },

    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: 2,
          borderRadius: tokens.radii.sm * 4,
          backgroundColor: tokens.colors.divider,
        },
        bar: {
          backgroundColor: tokens.colors.primary,
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radii.xs * 4,
          fontSize: '0.72rem',
          height: 28,
          transition: tokens.transition.fast,
        },
        sizeSmall: {
          height: 22,
          fontSize: '0.65rem',
        },
      },
    },

    MuiAvatar: {
      styleOverrides: {
        root: {
          width: 40,
          height: 40,
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: tokens.radii.sm * 4,
          border: `1px solid ${tokens.colors.divider}`,
        },
      },
    },

    MuiAlert: {
      defaultProps: {
        variant: 'outlined' as const,
      },
      styleOverrides: {
        root: {
          borderRadius: tokens.radii.xs * 4,
          fontSize: '0.8rem',
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: tokens.colors.textPrimary,
          fontSize: '0.65rem',
          borderRadius: tokens.radii.xs * 4,
        },
      },
    },

    MuiBadge: {
      styleOverrides: {
        badge: {
          fontSize: '0.6rem',
          minWidth: 16,
          height: 16,
        },
      },
    },
  },
});

export { tokens };
export default theme;
